import { lazy, Suspense, useEffect } from 'react';
import { ClerkProvider, useClerk } from '@clerk/react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router';
import { clerkAppearance } from './lib/clerk-appearance';
import { SessionProvider, useSession } from './lib/session';
import { reloadingImport } from './lib/stale-chunk';
import { useScheme } from './lib/theme';
import Shell from './pages/Shell';

// Every page loads with its route (finding unused-rest-8). The first load
// is the app's frame (session, Clerk, router, Shell) and the one page
// asked for; the rest, with what only they use (the charts on the SEO,
// newsroom and analytics pages, the customiser), follow in idle time once
// a signed-in page is up (PreloadPages), so moving between pages looks as
// it did when the console was one chunk: the next page shows at once,
// with its own loading state. Before that, the router changes location
// in a transition (react-router's BrowserRouter), so React keeps the last
// page up until the next one's code is in, rather than a blank. The
// ceiling on the first-load chunk is scripts/first-load.test.mjs.
const PAGE = {
  login: () => import('./pages/Login'),
  organisations: () => import('./pages/Organisations'),
  sites: () => import('./pages/Sites'),
  site: () => import('./pages/Site'),
  activity: () => import('./pages/Activity'),
  newsroom: () => import('./pages/Newsroom'),
  about: () => import('./public/About'),
  privacy: () => import('./public/Privacy'),
  history: () => import('./pages/History'),
  sitePosts: () => import('./pages/SitePosts'),
  settingsSection: () => import('./settings/sections'),
  seoLayout: () => import('./seo/SeoLayout'),
  seoSetup: () => import('./seo/Setup'),
  seoPages: () => import('./seo/pages'),
  members: () => import('./pages/Members'),
  contentNetwork: () => import('./pages/ContentNetwork'),
  contentNetworkMember: () => import('./pages/ContentNetworkMember'),
  contentNetworkStaff: () => import('./pages/ContentNetworkStaff'),
  contentNetworkAnalytics: () => import('./pages/ContentNetworkAnalytics'),
  fleetHealth: () => import('./pages/FleetHealth'),
  fleetSite: () => import('./pages/FleetSite'),
  orgSettings: () => import('./pages/OrgSettings'),
  customise: () => import('./pages/Customise'),
  siteSettings: () => import('./pages/SiteSettings'),
  tokens: () => import('./pages/Tokens'),
  data: () => import('./pages/Data'),
  actions: () => import('./pages/Actions'),
  openSite: () => import('./pages/OpenSite'),
};
/** A page's component, loaded with its route; a chunk a deploy has replaced reloads the tab once (lib/stale-chunk.ts). */
const page = <T extends React.ComponentType>(load: () => Promise<{ default: T }>) => lazy(reloadingImport(load));
const Login = page(PAGE.login);
const Organisations = page(PAGE.organisations);
const Sites = page(PAGE.sites);
const SitePage = page(PAGE.site);
const Activity = page(PAGE.activity);
const Newsroom = page(PAGE.newsroom);
const About = page(PAGE.about);
const Privacy = page(PAGE.privacy);
const History = page(PAGE.history);
const SitePosts = page(PAGE.sitePosts);
const SettingsSectionPage = page(PAGE.settingsSection);
const SeoLayout = page(PAGE.seoLayout);
const SeoSetup = page(PAGE.seoSetup);
const SeoPerformance = page(() => PAGE.seoPages().then((m) => ({ default: m.SeoPerformance })));
const SeoRecommendations = page(() => PAGE.seoPages().then((m) => ({ default: m.SeoRecommendations })));
const SeoOpportunities = page(() => PAGE.seoPages().then((m) => ({ default: m.SeoOpportunities })));
const SeoQueries = page(() => PAGE.seoPages().then((m) => ({ default: m.SeoQueries })));
const Members = page(PAGE.members);
const ContentNetwork = page(PAGE.contentNetwork);
const ContentNetworkMember = page(PAGE.contentNetworkMember);
const ContentNetworkStaff = page(PAGE.contentNetworkStaff);
const ContentNetworkAnalytics = page(PAGE.contentNetworkAnalytics);
const FleetHealth = page(PAGE.fleetHealth);
const FleetSite = page(PAGE.fleetSite);
const OrgSettings = page(PAGE.orgSettings);
const Customise = page(PAGE.customise);
const SiteSettings = page(PAGE.siteSettings);
const Tokens = page(PAGE.tokens);
const Data = page(PAGE.data);
const Actions = page(PAGE.actions);
const OpenSite = page(PAGE.openSite);

let preloadStarted = false;
/**
 * Fetches every page's code, one page at a time and each when the browser
 * is idle, after a signed-in page is up: the page asked for and its data
 * go first, and from then on no move between pages waits on the network
 * for code. It also narrows what a tab left open across a deploy can
 * miss: once it holds every page, it never asks for a chunk the deploy
 * has replaced (one it does ask for reloads the tab: lib/stale-chunk.ts).
 * Signed-out pages (login, about, privacy) never start it. Once per tab:
 * a module-level flag, not a ref, so StrictMode's second effect run and a
 * remounted shell don't start it again. A failed fetch is left for the
 * page's own load to retry.
 */
function PreloadPages() {
  useEffect(() => {
    if (preloadStarted) return;
    preloadStarted = true;
    const idle = () => new Promise<void>((done) => {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(() => done(), { timeout: 2000 });
      else setTimeout(done, 50);
    });
    void (async () => {
      for (const load of Object.values(PAGE)) {
        await idle();
        await load().catch(() => undefined);
      }
    })();
  }, []);
  return null;
}

/** What a first load shows until the app knows who is signed in, and then until the page's code is in: one note, so the two waits read as one. */
const loadingNote = <p className="cz-root p-6 text-sm text-fg-2">Loading…</p>;

/**
 * Hands Clerk's sign-out to the session, so signing out ends both. Has to
 * be a component rather than a call in SessionProvider: `useClerk` only
 * works inside ClerkProvider, and ClerkProvider needs the publishable key
 * that SessionProvider is the one to fetch.
 */
function ClerkSignOutBridge() {
  const { signOut } = useClerk();
  const { registerIdpSignOut } = useSession();
  useEffect(() => {
    registerIdpSignOut(() => signOut());
    return () => registerIdpSignOut(null);
  }, [signOut, registerIdpSignOut]);
  return null;
}

/**
 * Clerk wraps the whole app, not just /login. The account menu lives in
 * the shell, and signing out has to reach Clerk from there.
 * `clerk_publishable_key` is absent on an environment configured for
 * GitHub staff login only, where the app runs without Clerk entirely.
 */
function ClerkGate({ children }: { children: React.ReactNode }) {
  const { loading, config } = useSession();
  // Clerk is told the console's palette here, at the provider, so the
  // login card and the organisation switcher are painted alike — and
  // re-told when the scheme changes, which @clerk/react forwards to
  // clerk-js as the prop updates.
  const appearance = clerkAppearance(useScheme());
  // Not `null`: this sits above BrowserRouter, so rendering nothing blanks
  // every route rather than just delaying one.
  if (loading) return loadingNote;
  if (!config?.clerk_publishable_key) return <>{children}</>;
  return (
    // allowedRedirectOrigins: this origin only. Clerk ranks a
    // sign_in_force_redirect_url in the page's URL above Login's
    // forceRedirectUrl, and by default lets it name any
    // https://*.<the Frontend API's parent domain> host (every site's
    // public and preview-link host), so a crafted /login link would send
    // a fresh sign-in there instead of to the session exchange.
    <ClerkProvider publishableKey={config.clerk_publishable_key} afterSignOutUrl="/login" appearance={appearance} allowedRedirectOrigins={[window.location.origin]}>
      <ClerkSignOutBridge />
      {children}
    </ClerkProvider>
  );
}

function RequireSession({ children }: { children: React.ReactNode }) {
  const { loading, me } = useSession();
  const loc = useLocation();
  if (loading) return <p className="cz-root p-6 text-sm text-cz-muted">Loading…</p>;
  if (!me) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

/** A page that moved within the site: `to` is relative to the site, and the query goes with it. */
function Moved({ to }: { to: string }) {
  const { client, key } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/c/${client}/s/${key}/${to}${search}`} replace />;
}

/**
 * Search became SEO. The old address still works: a bookmark lands on
 * Performance, and Google's connect round trip from before the move
 * (`?google=…`) lands on Setup with its query intact, which is where its
 * banner is read.
 */
function SearchMoved() {
  const { client, key } = useParams();
  const { search } = useLocation();
  const to = new URLSearchParams(search).has('google') ? 'seo/setup' : 'seo';
  return <Navigate to={`/c/${client}/s/${key}/${to}${search}`} replace />;
}

export default function App() {
  return (
    <SessionProvider>
      <ClerkGate>
      <BrowserRouter>
        {/* A page outside the shell (login, the public pages, open-site) on
            its first load; a shell page's own wait is inside the shell. */}
        <Suspense fallback={loadingNote}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public, no session: the app's home page and privacy policy,
              which Google's OAuth verification needs on this domain. */}
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* The open-site route (pages/OpenSite): where a site's preview
              host sends an anonymous visitor. Signed in, it hands
              straight off to the API, so it gets no shell. Two spellings
              — the product emits `/clients/…/sites/…/open` for this and
              `/c/…/s/…` for every other console link — one page. */}
          <Route element={<RequireSession><Outlet /></RequireSession>}>
            <Route path="/clients/:client/sites/:key/open" element={<OpenSite />} />
            <Route path="/c/:client/s/:key/open" element={<OpenSite />} />
          </Route>
          <Route element={<RequireSession><PreloadPages /><Shell /></RequireSession>}>
            <Route path="/" element={<Organisations />} />
            {/* Fleet-wide, not org-scoped: an admin token belongs to no
                organisation, and the mistake worth catching is a token
                minted against the wrong one. */}
            <Route path="/tokens" element={<Tokens />} />
            <Route path="/content-network" element={<ContentNetworkStaff />} />
            <Route path="/fleet/health" element={<FleetHealth />} />
            <Route path="/fleet/health/:pool/:slug" element={<FleetSite />} />
            <Route path="/c/:client" element={<Sites />} />
            <Route path="/c/:client/members" element={<Members />} />
            <Route path="/c/:client/content-network" element={<ContentNetwork />} />
            <Route path="/c/:client/content-network/analytics" element={<ContentNetworkAnalytics />} />
            <Route path="/c/:client/content-network/:key" element={<ContentNetworkMember />} />
            <Route path="/c/:client/activity" element={<Activity />} />
            <Route path="/c/:client/newsroom" element={<Newsroom />} />
            <Route path="/c/:client/settings" element={<OrgSettings />} />
            <Route path="/c/:client/s/:key" element={<SitePage />} />
            <Route path="/c/:client/s/:key/settings" element={<SiteSettings />}>
              <Route index element={<Navigate to="general" replace />} />
              <Route path=":section" element={<SettingsSectionPage />} />
            </Route>
            <Route path="/c/:client/s/:key/history" element={<History />} />
            <Route path="/c/:client/s/:key/posts" element={<SitePosts />} />
            <Route path="/c/:client/s/:key/data" element={<Data />} />
            <Route path="/c/:client/s/:key/actions" element={<Actions />} />
            <Route path="/c/:client/s/:key/redirects" element={<Moved to="settings/redirects" />} />
            <Route path="/c/:client/s/:key/seo" element={<SeoLayout />}>
              <Route index element={<SeoPerformance />} />
              <Route path="recommendations" element={<SeoRecommendations />} />
              <Route path="opportunities" element={<SeoOpportunities />} />
              <Route path="queries" element={<SeoQueries />} />
              <Route path="setup" element={<SeoSetup />} />
            </Route>
            <Route path="/c/:client/s/:key/search" element={<SearchMoved />} />
            <Route path="/c/:client/s/:key/customise" element={<Customise />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </ClerkGate>
    </SessionProvider>
  );
}
